import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { JupiterSavedViewAddComponent } from '@modules/jupiter/home/@components/view-form/view-form.component';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { Store } from '@ngxs/store';
import SavedViewService from '@services/saved-view.service';
import { RelatedToType } from '@shared/enums';
import { filter } from 'rxjs';
import { TsButtonModule, TSIconModule, TSTreeMenuModule, TSTreeNode } from 'tsui';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { TsDividerComponent } from 'tsui';
import { LucideAngularModule } from 'lucide-angular';
import { iconMap } from '../functions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-shell-view-loader',
  standalone: true,
  imports: [TSTreeMenuModule, TsButtonModule, TSIconModule, TsDividerComponent, LucideAngularModule],
  templateUrl: './shell-view-loader.component.html',
  styleUrl: './shell-view-loader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellViewLoaderComponent {
  identifier = input.required<{ id: string; icon: string; urlPrefix: string }>();
  onNavigate = output<void>();
  isLoading = signal(true);
  views = signal<TSTreeNode[]>([]);
  label = signal<string>('');
  iconMap = iconMap;

  private readonly savedViewService = inject(SavedViewService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private customObject: CustomObject;

  delegateLeafClickFn(route: string): void {
    this.router.navigateByUrl(route);
    this.onNavigate.emit();
  }

  ngOnInit(): void {
    const relatedTo = this.getRelatedToFromIdentifier(this.identifier().id);

    this.savedViewService
      .get(relatedTo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((views) => {
        this.views.set(
          views.map((view) => {
            let link = '/space/void?return=/space/' + this.identifier().urlPrefix + '/' + view.id;

            return {
              id: String(view.id),
              name: view.name,
              icon: 'ts_list-ul',
              link,
            };
          }),
        );
        this.isLoading.set(false);
      });

    this.customObject = this.store.selectSnapshot(ObjectsManagerState.getByUrlPrefix(this.identifier().urlPrefix));

    this.label.set(this.customObject.label_plural);
  }

  openNewViewDialog(): void {
    this.matDialog
      .open(JupiterSavedViewAddComponent, {
        data: { saved_views: [] },
      })
      .afterClosed()
      .pipe(filter((result) => !!result))
      .subscribe((result: { name: string }) => {
        this.savedViewService
          .create({
            name: result.name,
            related_to_type: this.customObject.name,
            definition: JSON.stringify({ table: { fields: ['id'] } }),
          })
          .subscribe((view) => {
            const link = '/space/void?return=/space/' + this.identifier().urlPrefix + '/' + view.id;

            this.views.set([
              ...this.views(),
              {
                id: String(view.id),
                name: view.name,
                icon: 'ts_list-ul',
                link,
              },
            ]);
          });
      });
  }

  private getRelatedToFromIdentifier(identifier: string): string {
    switch (identifier) {
      case 'properties':
        return RelatedToType.Property;
      case 'companies':
        return RelatedToType.Company;
      case 'contacts':
        return RelatedToType.Contact;
      case 'leases':
        return RelatedToType.PropertyTenant;
    }
    return identifier;
  }
}
